import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material";

import useClientInvoice from "../../hooks/useClientInvoice";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";
import Dialog_YesNo from "../ui/Dialog_YesNo";

const BulkGenerateDialog = ({ open, onClose, onGenerated }) => {
  const { generateInvoices } = useClientInvoice();
  const { getFromServer } = useData();
  const { showMessage } = usePage();

  const [invoicePeriod, setInvoicePeriod] = useState(
    new Date().toISOString().slice(0, 7).replace("-", ""),
  );
  const [selectedClientProjects, setSelectedClientProjects] = useState({});
  const [clientProjects, setClientProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [regenerationWarning, setRegenerationWarning] = useState(null);
  const [confirmRegenerationKey, setConfirmRegenerationKey] = useState(null);

  useEffect(() => {
    if (open) {
      loadClientProjects();
    }
  }, [open]);

  const loadClientProjects = async () => {
    // Load all active clients and projects
    setLoading(true);
    const clients = await getFromServer({
      entityName: "Client",
      filter: { isActive: true },
    });

    const projects = await getFromServer({
      entityName: "Project",
      filter: { isActive: true },
    });

    // Build client-project combinations
    const clientProjectList = [];
    for (const client of clients || []) {
      for (const project of projects || []) {
        if (project.clientId === client.id) {
          // Only include if project has billingCallRate
          if (project.billingCallRate) {
            clientProjectList.push({
              clientId: client.id,
              projectId: project.id,
              clientName: client.name,
              projectName: project.name,
              billingCallRate: project.billingCallRate,
            });
          }
        }
      }
    }

    setClientProjects(clientProjectList);
    setLoading(false);
  };

  const handleToggleClientProject = (index) => {
    const key = `${clientProjects[index].clientId}_${clientProjects[index].projectId}`;
    setSelectedClientProjects({
      ...selectedClientProjects,
      [key]: !selectedClientProjects[key],
    });
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    const newSelected = {};
    clientProjects.forEach((cp) => {
      const key = `${cp.clientId}_${cp.projectId}`;
      newSelected[key] = checked;
    });
    setSelectedClientProjects(newSelected);
  };

  const handleGenerate = async () => {
    const selectedCount = Object.values(selectedClientProjects).filter(
      Boolean,
    ).length;
    if (selectedCount === 0) {
      showMessage(
        "Please select at least one client/project combination",
        "warning",
      );
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    let warningCount = 0;

    for (const cp of clientProjects) {
      const key = `${cp.clientId}_${cp.projectId}`;
      if (selectedClientProjects[key]) {
        const result = await generateInvoices(
          cp.clientId,
          cp.projectId,
          invoicePeriod,
          confirmRegenerationKey === key, // Pass checkNewItems flag if confirmed
        );
        if (result) {
          if (result.warning) {
            warningCount++;
            // Store first warning to display
            if (!regenerationWarning) {
              setRegenerationWarning({
                ...result,
                clientProjectKey: key,
              });
            }
          } else {
            successCount++;
          }
        } else {
          errorCount++;
        }
      }
    }

    setLoading(false);

    if (regenerationWarning && !confirmRegenerationKey) {
      // Stop here - user needs to confirm
      return;
    }

    const message = `Generated ${successCount} invoices${
      errorCount > 0 ? ` (${errorCount} failed)` : ""
    }${warningCount > 0 ? ` (${warningCount} required confirmation)` : ""}`;
    showMessage(message, errorCount > 0 ? "warning" : "success");
    onGenerated();
    onClose();
  };

  const handleConfirmRegeneration = () => {
    if (regenerationWarning) {
      setConfirmRegenerationKey(regenerationWarning.clientProjectKey);
      setRegenerationWarning(null);
      handleGenerate(); // Resume generation
    }
  };

  const handleClose = () => {
    setSelectedClientProjects({});
    setSelectAll(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Generate Monthly Invoices</DialogTitle>
      <DialogContent sx={{ paddingTop: 2 }}>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
        >
          {/* Period selector */}
          <Box>
            <Typography
              variant="subtitle2"
              marginBottom={1}
            >
              Invoice Period (YYYYMM)
            </Typography>
            <TextField
              type="text"
              value={invoicePeriod}
              onChange={(e) => setInvoicePeriod(e.target.value.slice(0, 6))}
              placeholder="202601"
              fullWidth
            />
            <Typography
              variant="caption"
              color="textSecondary"
            >
              Format: YYYYMM (e.g., 202601 for January 2026)
            </Typography>
          </Box>

          {/* Client/Project selection */}
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={1}
            >
              <Typography variant="subtitle2">
                Select Client/Projects to Generate Invoices
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Checkbox
                  checked={selectAll}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  disabled={clientProjects.length === 0}
                />
                <Typography variant="caption">Select All</Typography>
              </Box>
            </Box>

            {loading ? (
              <Typography color="textSecondary">Loading...</Typography>
            ) : clientProjects.length > 0 ? (
              <Card sx={{ maxHeight: 400, overflow: "auto" }}>
                <CardContent sx={{ padding: 0 }}>
                  <Table size="small">
                    <TableHead
                      sx={{
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                      }}
                    >
                      <TableRow>
                        <TableCell
                          align="center"
                          sx={{ width: 50 }}
                        >
                          <Checkbox
                            checked={selectAll}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Project</TableCell>
                        <TableCell align="right">Billing Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientProjects.map((cp, idx) => {
                        const key = `${cp.clientId}_${cp.projectId}`;
                        return (
                          <TableRow
                            key={key}
                            sx={{
                              backgroundColor: selectedClientProjects[key]
                                ? "#f0f0f0"
                                : "inherit",
                            }}
                          >
                            <TableCell align="center">
                              <Checkbox
                                checked={selectedClientProjects[key] || false}
                                onChange={() => handleToggleClientProject(idx)}
                              />
                            </TableCell>
                            <TableCell>{cp.clientName}</TableCell>
                            <TableCell>{cp.projectName}</TableCell>
                            <TableCell align="right">
                              ${cp.billingCallRate.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Typography color="textSecondary">
                No active projects with billing rates found
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={
            loading || Object.values(selectedClientProjects).every((v) => !v)
          }
        >
          Generate
        </Button>
      </DialogActions>

      <Dialog_YesNo
        open={Boolean(regenerationWarning)}
        title="Invoice Already Approved"
        message={
          regenerationWarning
            ? `Invoice ${regenerationWarning.invoiceNumber} (${regenerationWarning.invoiceStatus}) already exists for this period with new unbilled items. Would you like to regenerate it with the new items? This will unlock the invoice for editing.`
            : ""
        }
        onYes={handleConfirmRegeneration}
        onNo={() => setRegenerationWarning(null)}
        yesLabel="Regenerate"
        noLabel="Cancel"
      />
    </Dialog>
  );
};

export default BulkGenerateDialog;
